if (!customElements.get('appointment-booking-form')) {
  const APPT_AVAILABILITY_CACHE = new Map();
  const APPT_CACHE_TTL_MS = 120000;
  const APPT_INFLIGHT = new Map();

  function apptGetCachedAvailability(date) {
    const entry = APPT_AVAILABILITY_CACHE.get(date);
    if (!entry) return null;
    if (Date.now() - entry.ts > APPT_CACHE_TTL_MS) {
      APPT_AVAILABILITY_CACHE.delete(date);
      return null;
    }
    return entry.booked.slice();
  }

  function apptCacheAvailability(date, booked) {
    APPT_AVAILABILITY_CACHE.set(date, {
      booked: booked.slice(),
      ts: Date.now(),
    });
  }

  function apptMarkSlotBooked(date, time) {
    const normalized = time;
    const entry = APPT_AVAILABILITY_CACHE.get(date);
    if (entry) {
      if (!entry.booked.includes(normalized)) entry.booked.push(normalized);
      entry.ts = Date.now();
      return;
    }
    APPT_AVAILABILITY_CACHE.set(date, { booked: [normalized], ts: Date.now() });
  }

  function apptInvalidateAvailability(date) {
    APPT_AVAILABILITY_CACHE.delete(date);
  }

  // Loads the booked times for the next ~2 months in ONE request (the booking
  // script reads the calendar once for the whole range). Afterwards choosing a
  // date is instant, and each customer makes far fewer calls to the script.
  let APPT_RANGE_PROMISE = null;
  let APPT_RANGE_TS = 0;
  const APPT_RANGE_DAYS = 60;

  function apptPrefetchRange(instance) {
    if (!instance.hasAvailabilityApi || instance.isDesignMode) return null;
    if (APPT_RANGE_PROMISE && Date.now() - APPT_RANGE_TS < APPT_CACHE_TTL_MS) return APPT_RANGE_PROMISE;

    const first = new Date();
    const last = new Date();
    last.setDate(last.getDate() + APPT_RANGE_DAYS);
    APPT_RANGE_TS = Date.now();

    APPT_RANGE_PROMISE = instance
      .jsonpRequest(
        {
          action: 'availabilityrange',
          start: instance.formatDateISO(first),
          end: instance.formatDateISO(last),
          secret: instance.apiSecret,
        },
        { timeout: 15000 }
      )
      .then((data) => {
        if (data && data.ok && data.booked && typeof data.booked === 'object' && !Array.isArray(data.booked)) {
          Object.keys(data.booked).forEach((day) => {
            if (Array.isArray(data.booked[day])) apptCacheAvailability(day, data.booked[day]);
          });
        } else {
          APPT_RANGE_PROMISE = null;
        }
        return data;
      })
      .catch(() => {
        // Older booking script (no range support) or a slow start: fall back
        // to the normal one-date-at-a-time requests.
        APPT_RANGE_PROMISE = null;
        return null;
      });

    return APPT_RANGE_PROMISE;
  }

  function apptFetchAvailability(instance, date) {
    if (APPT_INFLIGHT.has(date)) return APPT_INFLIGHT.get(date);

    const cached = apptGetCachedAvailability(date);
    if (cached) return Promise.resolve({ ok: true, booked: cached });

    const attempt = () =>
      instance.jsonpRequest(
        { action: 'availability', date, secret: instance.apiSecret },
        { timeout: 12000 }
      );

    // One automatic retry: the first hit can be slow on an Apps Script cold
    // start, so we transparently try again before surfacing an error.
    const promise = attempt()
      .catch(() => attempt())
      .finally(() => APPT_INFLIGHT.delete(date));

    APPT_INFLIGHT.set(date, promise);
    return promise;
  }

  class AppointmentBookingForm extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.selectedDate = null;
      this.selectedTime = null;
      this.calendarMonth = new Date();
      this.allTimeSlots = [];
      this.bookedSlots = [];
      this.availabilityLoading = false;
      this.availabilityReady = false;
      this.availabilityRequestId = 0;
      this.modalObserver = null;
      this.isSubmitting = false;
      this.isDesignMode = this.dataset.designMode === 'true';
      this.presetMeetingType = null;
      this.lastCalendarLink = '';
      this.lastShopNotified = false;
    }

    connectedCallback() {
      this.closedDays = this.parseClosedDays(this.dataset.closedDays || '0,6');
      this.closedDayMessage =
        this.dataset.closedDayMessage || 'We are closed on Saturdays and Sundays. Please choose a weekday.';
      this.sectionId = this.dataset.sectionId;
      this.variantSourceSectionId = this.resolveVariantSourceSectionId();
      this.contactRequired = this.dataset.contactRequired === 'true';

      this.apiUrl = this.normalizeApiUrl(this.dataset.apiUrl || '');
      this.apiSecret = (this.dataset.apiSecret || '').trim();
      // Theme setting: when on, the booking script emails the company with a
      // meeting-type subject and Shopify's generic email is skipped.
      this.customCompanyEmail = this.dataset.customCompanyEmail === 'true';
      this.apiConfigured = this.dataset.apiConfigured === 'true';
      this.apiConnected = false;
      this.slotDuration = Number(this.dataset.slotDuration) || 30;
      this.timezone = this.dataset.timezone || 'Europe/London';
      this.msgSlotUnavailable =
        this.dataset.msgSlotUnavailable || 'This time is no longer available. Please choose another slot.';
      this.msgSlotBooked = this.dataset.msgSlotBooked || 'Booked';
      this.msgSlotsLoading = this.dataset.msgSlotsLoading || 'Checking available times…';
      this.msgSelectDateForTimes =
        this.dataset.msgSelectDateForTimes || 'Please select a date to view available times.';
      this.msgBooking = this.dataset.msgBooking || 'Reserving your appointment…';
      this.msgBookingError =
        this.dataset.msgBookingError || 'We could not confirm your appointment. Please try another time or contact us.';
      this.msgAddCalendar = this.dataset.msgAddCalendar || 'Add to Google Calendar';
      this.msgMeetingType = this.dataset.msgMeetingType || 'Please choose how you would like to meet.';
      this.msgBookingAs = this.dataset.msgBookingAs || 'Booking: {{ label }}';
      this.msgBookedTitle = this.dataset.msgBookedTitle || 'Your appointment is booked';
      this.msgBookedDetails = this.dataset.msgBookedDetails || '{{ date }} at {{ time }}';
      this.msgBookedEmailNote =
        this.dataset.msgBookedEmailNote || 'A confirmation email has been sent to {{ email }}.';
      this.msgBookedNoEmailNote =
        this.dataset.msgBookedNoEmailNote || 'We will be in touch shortly to confirm your appointment.';
      this.msgBookedAnother = this.dataset.msgBookedAnother || 'Book another appointment';
      this.msgBookedClose = this.dataset.msgBookedClose || 'Close';
      this.skipMeetingType = this.dataset.skipMeetingType === 'true';

      this.steps = this.querySelectorAll('[data-appointment-step]');
      this.indicators = this.querySelectorAll('[data-step-indicator]');
      this.presetNote = this.querySelector('[data-appointment-preselected-note]');
      this.form = this.querySelector('form');

      // Shopify's contact-form spam protection (hCaptcha) adds its own submit
      // listener to every contact form and, after the captcha check, submits
      // the form natively. That posted a second, generic "New customer
      // message" email to the company (and reloaded the page) on top of our
      // own booking flow. This form is submitted only by this script, so
      // third-party calls to form.submit() are ignored and our fallback uses
      // the browser's real submit instead.
      if (this.form && !this.nativeFormSubmit) {
        const nativeSubmit = HTMLFormElement.prototype.submit;
        const form = this.form;
        this.nativeFormSubmit = () => nativeSubmit.call(form);
        try {
          form.submit = () => {};
        } catch (error) {
          /* property could not be shadowed — stopImmediatePropagation still applies */
        }
      }

      this.dateHidden = this.querySelector('[data-appointment-date]');
      this.timeHidden = this.querySelector('[data-appointment-time]');
      this.fieldDate = this.querySelector('[data-appointment-field-date]');
      this.fieldTime = this.querySelector('[data-appointment-field-time]');
      this.fieldMeetingType = this.querySelector('[data-appointment-field-meeting-type]');
      this.meetingTypeHidden = this.querySelector('[data-appointment-meeting-type]');
      this.meetingTypeOptions = this.querySelectorAll('[data-appointment-meeting-option]');
      this.fieldVariant = this.querySelector('[data-appointment-field-variant]');
      this.fieldProduct = this.querySelector('[data-appointment-field-product]');
      this.fieldUrl = this.querySelector('[data-appointment-field-url]');
      this.variantTitle = this.querySelector('[data-appointment-variant-title]');
      this.productPrice = this.querySelector('[data-appointment-product-price]');
      this.phoneInput = this.querySelector('[data-appointment-phone]');
      this.submitButton = this.querySelector('[type="submit"]');

      this.calGrid = this.querySelector('[data-cal-grid]');
      this.calWeekdays = this.querySelector('[data-cal-weekdays]');
      this.calMonthYear = this.querySelector('[data-cal-month-year]');
      this.calSelected = this.querySelector('[data-cal-selected]');
      this.timeSlotsContainer = this.querySelector('[data-time-slots]');
      this.nextButton = this.querySelector('[data-appointment-next]');
      this.timePickerSection = this.querySelector('[data-appointment-time-picker]');

      this.initCalendar();
      this.initTimeSlots();
      this.bindMeetingTypeChoice();
      this.bindEvents();
      this.bindVariantChange();
      this.bindModalOpen();
      this.showCalendarLinkIfSuccess();
      this.updatePhoneRequirement();
      if (this.skipMeetingType) this.collapseMeetingTypeStep();
      if (this.hasAvailabilityApi) {
        this.apiConnected = true;
      }
    }

    // Visually collapses the 3-step indicator (Date/Time, Meeting type, Details)
    // down to 2 steps (Date/Time, Details) when the meeting type is chosen
    // beforehand (e.g. a "Visit options" card). The meeting-type step markup is
    // left untouched — goToStep() simply never activates it in this mode.
    collapseMeetingTypeStep() {
      const children = Array.from(this.querySelectorAll('.appointment-steps > *'));
      const meetingIndicator = children.find((el) => el.dataset && el.dataset.stepIndicator === '2');
      const meetingIndex = meetingIndicator ? children.indexOf(meetingIndicator) : -1;
      if (meetingIndex < 1) return;

      // Hide the divider right before the meeting-type indicator, and the
      // indicator itself, leaving "Date/Time — divider — Details" visually.
      children[meetingIndex - 1].setAttribute('hidden', '');
      meetingIndicator.setAttribute('hidden', '');

      const detailsIndicator = children.find((el) => el.dataset && el.dataset.stepIndicator === '3');
      const detailsNumber = detailsIndicator?.querySelector('.appointment-step-indicator__number');
      if (detailsNumber) detailsNumber.textContent = '2';
    }

    // Sets the meeting type before the popup opens, so the "how would you like
    // to meet" step can be skipped entirely (data-skip-meeting-type="true").
    setPresetMeetingType(value, label) {
      const safeValue = (value || '').trim();
      const safeLabel = (label || safeValue).trim();
      this.presetMeetingType = { value: safeValue, label: safeLabel };

      if (this.meetingTypeHidden) this.meetingTypeHidden.value = safeValue;
      if (this.fieldMeetingType) this.fieldMeetingType.value = safeLabel;
      this.updatePhoneRequirement();

      if (this.presetNote) {
        this.presetNote.textContent = safeLabel ? this.msgBookingAs.replace('{{ label }}', safeLabel) : '';
        this.presetNote.hidden = !safeLabel;
      }
    }

    normalizeApiUrl(url) {
      let value = (url || '').trim();
      if (!value) return '';
      if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
      return value.replace(/\/dev$/, '/exec');
    }

    userFriendlyError(code) {
      if (code === 'slot_unavailable') return this.msgSlotUnavailable;
      if (code === 'server_busy') return 'Our booking system is busy. Please wait a moment and try again.';
      return this.msgBookingError;
    }

    async testApiConnection() {
      if (!this.hasAvailabilityApi || !this.isDesignMode) return true;

      try {
        const data = await this.jsonpRequest({ action: 'ping', secret: this.apiSecret });
        return Boolean(data && data.ok);
      } catch (error) {
        return false;
      }
    }

    showFormAlert(message, { error = true } = {}) {
      if (!message) return;
      this.clearFormAlerts();
      const alert = document.createElement('div');
      alert.className = `appointment-form-alert${error ? ' appointment-form-alert--error' : ' appointment-form-alert--success'}`;
      alert.setAttribute('role', 'alert');
      alert.setAttribute('data-appointment-form-alert', '');
      alert.textContent = message;
      this.insertBefore(alert, this.firstChild);
      alert.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    clearFormAlerts() {
      this.querySelectorAll('[data-appointment-form-alert]').forEach((el) => el.remove());
      this.clearApiStatus();
    }

    bindModalOpen() {
      const modal = this.closest('modal-dialog');
      if (!modal) {
        // Inline form: load the coming weeks' availability once it is near the
        // viewport (not for visitors who never scroll to it).
        if (this.hasAvailabilityApi && !this.isDesignMode) {
          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
              (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                observer.disconnect();
                apptPrefetchRange(this);
              },
              { rootMargin: '400px' }
            );
            observer.observe(this);
          } else {
            apptPrefetchRange(this);
          }
        }
        return;
      }

      this.modalObserver = new MutationObserver(() => {
        if (!modal.hasAttribute('open')) return;
        apptPrefetchRange(this);
        this.clearFormAlerts();
        this.clearErrors();
        if (this.selectedDate) {
          this.availabilityLoading = true;
          this.availabilityReady = false;
          this.renderTimeSlots();
          this.updateNextButtonState();
          apptInvalidateAvailability(this.formatDateISO(this.selectedDate));
          this.refreshAvailability(this.formatDateISO(this.selectedDate), { force: true }).then(() => {
            this.availabilityLoading = false;
            this.availabilityReady = true;
            this.renderTimeSlots();
            this.updateNextButtonState();
          });
        }
      });

      this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    resolveVariantSourceSectionId() {
      const raw = (this.dataset.variantSyncSection || '').trim();
      if (raw === 'auto' || raw === '') {
        const pi = document.querySelector('product-info[data-section]');
        if (pi?.dataset.section) return pi.dataset.section;
      } else if (raw) {
        return raw;
      }
      return this.dataset.sectionId;
    }

    parseClosedDays(value) {
      return value
        .split(',')
        .map((day) => Number(day.trim()))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
    }

    isClosedDay(date) {
      return this.closedDays.includes(date.getDay());
    }

    get hasAvailabilityApi() {
      return Boolean(this.apiUrl);
    }

    jsonpRequest(params, { timeout = 8000 } = {}) {
      return new Promise((resolve, reject) => {
        const callbackName = `apptJsonp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        let url;

        try {
          url = new URL(this.apiUrl);
        } catch (error) {
          reject(new Error('Invalid API URL'));
          return;
        }

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && String(value).length) {
            url.searchParams.set(key, String(value));
          }
        });
        url.searchParams.set('callback', callbackName);

        const script = document.createElement('script');
        let settled = false;

        const cleanup = () => {
          if (script.parentNode) script.remove();
          // A slow response can still arrive after a timeout (the request is
          // already in flight even once the script tag is removed). Leave a
          // harmless no-op under the callback name for a while so that late
          // reply does not throw "callback is not defined" in the console.
          window[callbackName] = () => {};
          window.setTimeout(() => {
            try {
              delete window[callbackName];
            } catch (deleteError) {
              window[callbackName] = undefined;
            }
          }, 120000);
        };

        const finish = (handler) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          cleanup();
          handler();
        };

        window[callbackName] = (data) => {
          finish(() => resolve(data));
        };

        script.src = url.toString();
        script.async = true;
        script.onerror = () => {
          finish(() => reject(new Error('Request failed')));
        };
        document.head.appendChild(script);

        const timeoutId = window.setTimeout(() => {
          finish(() => reject(new Error('timeout')));
        }, timeout);
      });
    }

    showApiStatus(message, isError) {
      this.showFormAlert(message, { error: isError });
    }

    clearApiStatus() {
      /* handled by clearFormAlerts */
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.modalObserver) this.modalObserver.disconnect();
    }

    bindMeetingTypeChoice() {
      if (!this.meetingTypeOptions || !this.meetingTypeOptions.length) return;

      this.meetingTypeOptions.forEach((option) => {
        option.addEventListener('click', () => {
          const value = option.dataset.meetingType || 'in_person';
          const label = option.dataset.meetingLabel || '';

          this.meetingTypeOptions.forEach((el) => {
            const isSelected = el === option;
            el.classList.toggle('is-selected', isSelected);
            el.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          });

          if (this.meetingTypeHidden) this.meetingTypeHidden.value = value;
          if (this.fieldMeetingType && label) this.fieldMeetingType.value = label;
          this.updatePhoneRequirement();

          const section = this.querySelector('[data-appointment-meeting-type-section]');
          section?.querySelectorAll('.appointment-field-error').forEach((el) => el.remove());
        });
      });
    }

    bindEvents() {
      this.querySelectorAll('[data-appointment-goto]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const target = Number(btn.getAttribute('data-appointment-goto'));
          if (target) this.goToStep(target);
        });
      });

      this.querySelector('[data-cal-prev]')?.addEventListener('click', () => {
        this.calendarMonth.setMonth(this.calendarMonth.getMonth() - 1);
        this.renderCalendar();
      });

      this.querySelector('[data-cal-next]')?.addEventListener('click', () => {
        this.calendarMonth.setMonth(this.calendarMonth.getMonth() + 1);
        this.renderCalendar();
      });

      document.body.addEventListener('modalClosed', () => {
        const modal = this.closest('modal-dialog');
        if (!modal) return;
        if (modal.hasAttribute('open')) return;
        this.resetForm();
      });

      this.form?.addEventListener('submit', (event) => {
        // Registered before Shopify's captcha listener, so this stops it from
        // also handling (and natively submitting) the booking form.
        event.stopImmediatePropagation();
        this.handleSubmit(event);
      });
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      if (!this.validateStep2()) return;

      this.syncHiddenFields();
      this.syncCheckboxFields();

      if (!this.hasAvailabilityApi) {
        this.isSubmitting = true;
        this.setSubmitLoading(true);
        try {
          await this.completeBooking();
        } finally {
          this.isSubmitting = false;
          this.setSubmitLoading(false);
        }
        return;
      }

      this.clearFormAlerts();

      // No separate availability pre-check here: the server books under a lock
      // and returns slot_unavailable if the time was just taken. That avoids a
      // second round-trip on every submit and is race-safe under concurrency.
      this.isSubmitting = true;
      this.setSubmitLoading(true);

      try {
        const booked = await this.reserveSlot();
        if (!booked) return;

        await this.completeBooking();
      } finally {
        this.isSubmitting = false;
        this.setSubmitLoading(false);
      }
    }

    // Runs once the slot is reserved. Sends the Shopify contact email in the
    // background (so the page does not reload), then closes the popup, resets
    // the form so the customer can book again, and shows the confirmation on
    // the outer page. If the background send cannot be confirmed we fall back
    // to the normal form submit so the email is never lost.
    async completeBooking() {
      const summary = this.getBookingSummary();
      const calendarLink = this.lastCalendarLink;

      // The booking script already emailed the company (with a subject based on
      // the meeting type), so Shopify's generic "New customer message" email
      // is skipped to avoid a duplicate. If that email did not go out, the
      // Shopify contact email is still sent as the safety net.
      const shopNotified = this.lastShopNotified;
      this.lastShopNotified = false;
      if (window.console) {
        console.info(
          shopNotified
            ? '[appointment] Custom company email sent by the booking script — Shopify email skipped.'
            : `[appointment] Custom company email not sent (setting ${
                this.customCompanyEmail ? 'ON but the script did not confirm it' : 'OFF'
              }) — sending Shopify email.`
        );
      }
      const sent = shopNotified ? true : await this.submitContactForm();

      if (!sent) {
        // Remember the booking so the page that loads after the normal submit
        // can still show the outer-page "booked" banner with its details.
        try {
          sessionStorage.setItem('appt-booked-summary', JSON.stringify(summary));
          if (calendarLink) sessionStorage.setItem(`appt-cal-${this.sectionId}`, calendarLink);
        } catch (error) {
          /* storage unavailable — banner still shows without the extra details */
        }
        this.stripEmptyContactFields();
        if (this.nativeFormSubmit) this.nativeFormSubmit();
        else this.form.submit();
        return;
      }

      try {
        sessionStorage.removeItem(`appt-cal-${this.sectionId}`);
      } catch (error) {
        /* storage unavailable — nothing to clean up */
      }
      this.lastCalendarLink = '';

      const modal = this.closest('modal-dialog');
      const opener = modal ? modal.openedBy : null;
      if (modal && modal.hasAttribute('open') && typeof modal.hide === 'function') modal.hide();

      this.resetForm();
      this.showBookedBanner(summary, { modal, opener, calendarLink });
    }

    getBookingSummary() {
      const date = this.selectedDate
        ? this.selectedDate.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : this.dateHidden?.value || '';

      return {
        date,
        time: this.timeHidden?.value || '',
        type: (this.fieldMeetingType?.value || '').trim(),
        email: (this.querySelector('[data-appointment-email]')?.value || '').trim(),
      };
    }

    // Same payload the browser would post, minus empty optional contact fields
    // (so blank phone/variant never show in the Shopify email). Built from a
    // copy, so the live form keeps all its field names for the next booking.
    buildContactFormData() {
      const data = new FormData(this.form);
      Array.from(data.keys()).forEach((key) => {
        if (key.indexOf('contact[') !== 0 || key === 'contact[email]') return;
        const value = data.get(key);
        if (typeof value === 'string' && !value.trim()) data.delete(key);
      });
      return data;
    }

    async submitContactForm() {
      if (!this.form || typeof window.fetch !== 'function') return false;
      try {
        const action = (this.form.getAttribute('action') || '/contact').split('#')[0];
        const response = await fetch(action, {
          method: 'POST',
          body: this.buildContactFormData(),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        // A successful contact post is always answered with a redirect back to
        // the page with ?contact_posted=true. Be tolerant of the exact URL:
        // any redirect that is not a bot-check ("/challenge") counts too. A
        // rejected post (validation errors) is a plain 200 with no redirect.
        const posted = /[?&]contact_posted=true/.test(response.url);
        const redirectedOk = response.redirected && !/\/challenge/.test(response.url);
        const success = Boolean(response.ok && (posted || redirectedOk));
        if (!success && window.console) {
          console.warn('[appointment] Contact email could not be confirmed in the background:', {
            status: response.status,
            redirected: response.redirected,
            url: response.url,
          });
        }
        return success;
      } catch (error) {
        if (window.console) console.warn('[appointment] Background contact send failed:', error);
        return false;
      }
    }

    showBookedBanner(summary, { modal, opener, calendarLink } = {}) {
      document.querySelectorAll('[data-appointment-booked-banner]').forEach((el) => el.remove());

      const banner = document.createElement('div');
      banner.className = 'appointment-booked-banner';
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.setAttribute('data-appointment-booked-banner', '');

      const icon = document.createElement('span');
      icon.className = 'appointment-booked-banner__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg viewBox="0 0 20 20" fill="none"><path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const content = document.createElement('div');
      content.className = 'appointment-booked-banner__content';

      const title = document.createElement('p');
      title.className = 'appointment-booked-banner__title';
      title.textContent = this.msgBookedTitle;
      content.appendChild(title);

      if (summary.date || summary.time) {
        const details = this.msgBookedDetails
          .replace('{{ date }}', summary.date)
          .replace('{{ time }}', summary.time);
        const detailsEl = document.createElement('p');
        detailsEl.className = 'appointment-booked-banner__text';
        detailsEl.textContent = summary.type ? `${details} · ${summary.type}` : details;
        content.appendChild(detailsEl);
      }

      const note = document.createElement('p');
      note.className = 'appointment-booked-banner__text';
      note.textContent =
        this.hasAvailabilityApi && summary.email
          ? this.msgBookedEmailNote.replace('{{ email }}', summary.email)
          : this.msgBookedNoEmailNote;
      content.appendChild(note);

      const actions = document.createElement('div');
      actions.className = 'appointment-booked-banner__actions';

      const dismiss = () => {
        banner.classList.remove('is-visible');
        window.setTimeout(() => banner.remove(), 300);
      };

      const another = document.createElement('button');
      another.type = 'button';
      another.className = 'appointment-booked-banner__btn appointment-booked-banner__btn--primary';
      another.textContent = this.msgBookedAnother;
      another.addEventListener('click', () => {
        dismiss();
        if (modal && typeof modal.show === 'function') {
          modal.show(opener);
        } else {
          this.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      });
      actions.appendChild(another);

      if (calendarLink) {
        const calendar = document.createElement('a');
        calendar.className = 'appointment-booked-banner__btn';
        calendar.href = calendarLink;
        calendar.target = '_blank';
        calendar.rel = 'noopener noreferrer';
        calendar.textContent = this.msgAddCalendar;
        actions.appendChild(calendar);
      }

      content.appendChild(actions);

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'appointment-booked-banner__close';
      close.setAttribute('aria-label', this.msgBookedClose);
      close.innerHTML =
        '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      close.addEventListener('click', dismiss);

      banner.appendChild(icon);
      banner.appendChild(content);
      banner.appendChild(close);
      document.body.appendChild(banner);

      window.requestAnimationFrame(() => banner.classList.add('is-visible'));
    }

    setSubmitLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (loading) {
        this.submitButton.dataset.originalText = this.submitButton.textContent;
        this.submitButton.textContent = this.msgBooking;
      } else if (this.submitButton.dataset.originalText) {
        this.submitButton.textContent = this.submitButton.dataset.originalText;
      }
    }

    bindVariantChange() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || data.sectionId !== this.variantSourceSectionId) return;
        this.updateFromVariant(data.variant, data.html);
      });
    }

    initCalendar() {
      const compact = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 549px)').matches;
      const weekdays = compact
        ? ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      if (this.calWeekdays) {
        this.calWeekdays.innerHTML = weekdays
          .map((day) => `<span class="appointment-date-picker__weekday">${day}</span>`)
          .join('');
      }
      this.renderCalendar();
    }

    renderCalendar() {
      if (!this.calGrid || !this.calMonthYear) return;

      const year = this.calendarMonth.getFullYear();
      const month = this.calendarMonth.getMonth();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];

      const compactTitle =
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 549px)').matches;
      this.calMonthYear.textContent = compactTitle
        ? new Date(year, month).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
        : `${monthNames[month]} ${year}`;

      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let html = '';

      for (let i = 0; i < firstDay; i += 1) {
        html += '<span class="appointment-date-picker__day is-empty" aria-hidden="true"></span>';
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        const isPast = date < today;
        const isClosed = this.isClosedDay(date);
        const isToday = date.getTime() === today.getTime();
        const isSelected =
          this.selectedDate &&
          this.selectedDate.getFullYear() === year &&
          this.selectedDate.getMonth() === month &&
          this.selectedDate.getDate() === day;

        const classes = ['appointment-date-picker__day'];
        if (isToday) classes.push('is-today');
        if (isSelected) classes.push('is-selected');
        if (isClosed) classes.push('is-closed');

        const isDisabled = isPast || isClosed;
        const ariaLabel = isClosed
          ? `${monthNames[month]} ${day}, ${year} — ${this.closedDayMessage}`
          : `${monthNames[month]} ${day}, ${year}`;

        html += `<button type="button" class="${classes.join(' ')}" data-day="${day}" ${
          isDisabled ? 'disabled' : ''
        } aria-label="${ariaLabel}">${day}</button>`;
      }

      this.calGrid.innerHTML = html;

      this.calGrid.querySelectorAll('[data-day]:not([disabled])').forEach((btn) => {
        btn.addEventListener('click', () => {
          const day = Number(btn.dataset.day);
          this.selectDate(new Date(year, month, day));
        });
      });
    }

    async selectDate(date) {
      this.selectedDate = date;
      this.selectedTime = null;
      if (this.dateHidden) this.dateHidden.value = this.formatDateISO(date);
      if (this.timeHidden) this.timeHidden.value = '';
      this.updateSelectedDateLabel();
      this.renderCalendar();
      this.clearErrors();
      this.clearFormAlerts();

      const dateIso = this.formatDateISO(date);

      if (!this.hasAvailabilityApi) {
        this.availabilityReady = true;
        this.bookedSlots = [];
        this.renderTimeSlots();
        this.updateNextButtonState();
        this.scrollToTimePicker();
        return;
      }

      this.availabilityLoading = true;
      this.availabilityReady = false;
      this.bookedSlots = [];
      this.renderTimeSlots();
      this.updateNextButtonState();
      this.scrollToTimePicker();

      // Uses the times prefetched for the whole month when available (instant);
      // otherwise asks the booking script for just this date.
      await this.refreshAvailability(dateIso, { force: true, useCache: true });

      this.availabilityLoading = false;
      this.availabilityReady = this.apiConnected;
      this.renderTimeSlots();
      this.updateNextButtonState();
      this.scrollToTimePicker({ ifNeeded: true });
    }

    getScrollContainer() {
      return this.closest('.appointment-modal__body');
    }

    prefersReducedMotion() {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    // Scrolls the popup body (or the page, for the inline form) so `target`
    // is in view. Native scrollIntoView is avoided inside the modal because
    // it can also scroll the storefront behind the overlay.
    scrollTargetIntoView(target, { ifNeeded = false, offset = 12 } = {}) {
      if (!target) return;

      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      const run = () => {
        const container = this.getScrollContainer();
        if (container) {
          const cRect = container.getBoundingClientRect();
          const tRect = target.getBoundingClientRect();
          const fullyVisible =
            tRect.top >= cRect.top + 4 && tRect.bottom <= cRect.bottom - 8;
          if (ifNeeded && fullyVisible) return;
          container.scrollTo({
            top: Math.max(0, container.scrollTop + (tRect.top - cRect.top) - offset),
            behavior,
          });
          return;
        }

        const rect = target.getBoundingClientRect();
        const fullyVisible = rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
        if (ifNeeded && fullyVisible) return;
        target.scrollIntoView({ block: 'start', behavior, inline: 'nearest' });
      };

      window.requestAnimationFrame(run);
    }

    // After a date is picked the calendar still fills short phone screens, so
    // the time list sits below the fold. Bring the "Preferred time" block up.
    scrollToTimePicker(options = {}) {
      const target =
        this.timePickerSection?.closest('.appointment-form-section') || this.timePickerSection;
      this.scrollTargetIntoView(target, { offset: 10, ...options });
    }

    updateNextButtonState() {
      if (!this.nextButton) return;
      const canProceed =
        !this.hasAvailabilityApi || (this.availabilityReady && !this.availabilityLoading);
      this.nextButton.disabled = !canProceed;
      this.nextButton.setAttribute('aria-disabled', canProceed ? 'false' : 'true');
    }

    async refreshAvailability(dateIso, { force = false, useCache = false } = {}) {
      if (!this.hasAvailabilityApi || !dateIso) return true;

      const requestId = ++this.availabilityRequestId;

      try {
        if (force && !useCache) apptInvalidateAvailability(dateIso);
        const data = await apptFetchAvailability(this, dateIso);
        if (requestId !== this.availabilityRequestId) return true;

        if (data.ok && Array.isArray(data.booked)) {
          this.bookedSlots = data.booked.map((slot) => this.normalizeTime(slot)).filter(Boolean);
          apptCacheAvailability(dateIso, this.bookedSlots);
          this.apiConnected = true;

          if (this.selectedTime && this.bookedSlots.includes(this.normalizeTime(this.selectedTime))) {
            this.selectedTime = null;
            if (this.timeHidden) this.timeHidden.value = '';
          }

          return true;
        }

        if (force) {
          this.showFormAlert(this.userFriendlyError(data.error), { error: true });
        }
        this.apiConnected = false;
        return false;
      } catch (error) {
        if (force) {
          this.showFormAlert(this.msgBookingError, { error: true });
        }
        this.apiConnected = false;
        return false;
      }
    }

    updateSelectedDateLabel() {
      if (!this.calSelected || !this.selectedDate) return;
      const formatted = this.selectedDate.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      this.calSelected.textContent = formatted;
      this.calSelected.hidden = false;
    }

    formatDateISO(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    initTimeSlots() {
      if (!this.timeSlotsContainer) return;

      const raw = this.dataset.timeSlots || '';
      let slots = raw
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);

      if (!slots.length) {
        slots = [];
        for (let hour = 9; hour <= 17; hour += 1) {
          slots.push(`${String(hour).padStart(2, '0')}:00`);
          if (hour < 17) slots.push(`${String(hour).padStart(2, '0')}:30`);
        }
      }

      this.allTimeSlots = slots;
      this.availabilityReady = !this.hasAvailabilityApi;
      this.renderTimeSlots();
      this.updateNextButtonState();
    }

    renderTimeSlots() {
      if (!this.timeSlotsContainer) return;

      if (this.hasAvailabilityApi && !this.selectedDate) {
        this.timeSlotsContainer.innerHTML = `<p class="appointment-time-picker__status" role="status">${this.msgSelectDateForTimes}</p>`;
        return;
      }

      if (this.hasAvailabilityApi && (this.availabilityLoading || !this.availabilityReady)) {
        this.timeSlotsContainer.innerHTML = `
          <div class="appointment-time-picker__loading" role="status" aria-live="polite" aria-busy="true">
            <span class="appointment-time-picker__spinner" aria-hidden="true"></span>
            <span>${this.msgSlotsLoading}</span>
          </div>`;
        if (this.timePickerSection) {
          this.timePickerSection.classList.add('is-loading');
        }
        return;
      }

      if (this.hasAvailabilityApi && !this.availabilityLoading && !this.availabilityReady) {
        this.timeSlotsContainer.innerHTML = `<p class="appointment-time-picker__status" role="alert">${this.msgBookingError}</p>`;
        return;
      }

      if (this.timePickerSection) {
        this.timePickerSection.classList.remove('is-loading');
      }

      this.timeSlotsContainer.innerHTML = this.allTimeSlots
        .map((slot) => {
          const label = this.formatTimeLabel(slot);
          const isBooked = this.bookedSlots.includes(this.normalizeTime(slot));
          const isSelected = this.selectedTime === slot;
          const classes = ['appointment-time-slot'];
          if (isSelected) classes.push('is-selected');
          if (isBooked) classes.push('is-unavailable');

          return `<button type="button" class="${classes.join(' ')}" data-time-value="${slot}" role="option" aria-selected="${isSelected}" ${
            isBooked ? 'disabled aria-disabled="true"' : ''
          }>${label}${isBooked ? `<span class="appointment-time-slot__badge">${this.msgSlotBooked}</span>` : ''}</button>`;
        })
        .join('');

      this.timeSlotsContainer.querySelectorAll('.appointment-time-slot:not([disabled])').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.hasAvailabilityApi && !this.availabilityReady) return;

          this.timeSlotsContainer.querySelectorAll('.appointment-time-slot').forEach((el) => {
            el.classList.remove('is-selected');
            el.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-selected', 'true');
          this.selectedTime = btn.dataset.timeValue;
          if (this.timeHidden) this.timeHidden.value = this.selectedTime;
          this.updateNextButtonState();
          this.scrollTargetIntoView(this.nextButton, { ifNeeded: true, offset: 16 });
        });
      });
    }

    normalizeTime(value) {
      const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
      if (!match) return '';
      const hour = String(Number(match[1])).padStart(2, '0');
      const minute = String(Number(match[2])).padStart(2, '0');
      return `${hour}:${minute}`;
    }

    formatTimeLabel(slot) {
      const [h, m] = slot.split(':').map(Number);
      if (Number.isNaN(h)) return slot;
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${String(m || 0).padStart(2, '0')} ${period}`;
    }

    async loadAvailabilityForSelectedDate(options = {}) {
      if (!this.hasAvailabilityApi || !this.selectedDate) {
        this.bookedSlots = [];
        return true;
      }

      const date = this.formatDateISO(this.selectedDate);
      await this.refreshAvailability(date, { force: Boolean(options.force) });
      return true;
    }

    async validateSelectedSlotAvailable() {
      if (!this.hasAvailabilityApi) return true;

      apptInvalidateAvailability(this.formatDateISO(this.selectedDate));
      await this.refreshAvailability(this.formatDateISO(this.selectedDate), { force: true });
      this.renderTimeSlots();

      const slot = this.normalizeTime(this.selectedTime);
      if (this.bookedSlots.includes(slot)) {
        this.showError(this.querySelector('[data-appointment-time-picker]'), this.msgSlotUnavailable);
        this.showFormAlert(this.msgSlotUnavailable, { error: true });
        this.goToStep(1);
        return false;
      }

      return true;
    }

    collectBookingPayload() {
      const nameInput =
        this.form?.querySelector('[data-appointment-name]') ||
        this.form?.querySelector('input[autocomplete="name"]');
      const emailInput =
        this.form?.querySelector('[data-appointment-email]') ||
        this.form?.querySelector('[name="contact[email]"]');

      const customNotes = [];
      this.querySelectorAll('[data-appointment-custom-field]').forEach((field) => {
        const label = field.querySelector('.field__label, .appointment-choice-field__legend');
        const labelText = label?.textContent?.replace('*', '').trim();
        if (!labelText) return;

        const type = field.dataset.fieldType;
        if (type === 'checkbox') {
          const checked = field.querySelectorAll('[data-appointment-custom-checkbox]:checked');
          if (checked.length) {
            customNotes.push(`${labelText}: ${Array.from(checked).map((el) => el.value).join(', ')}`);
          }
        } else if (type === 'radio') {
          const selected = field.querySelector('[type="radio"]:checked');
          if (selected) customNotes.push(`${labelText}: ${selected.value}`);
        } else {
          const input = field.querySelector('[data-appointment-custom-input], select, input[type="text"]');
          if (input?.value?.trim()) customNotes.push(`${labelText}: ${input.value.trim()}`);
        }
      });

      // Pieces gathered in the private viewing tray. Added to the notes so the
      // calendar event and the shop notification list them, the same way the
      // hidden form field puts them in the Shopify enquiry email.
      const viewingPieces = (window.ViewingTray?.availableItems || [])
        .map((piece) => piece.title)
        .filter(Boolean);
      if (viewingPieces.length) {
        customNotes.push(`Private viewing: ${viewingPieces.join(', ')}`);
      }

      // How a customer in proposal mode wants to be contacted. Goes in first so
      // it survives the notes length cap.
      const proposalNote = window.ProposalMode?.bookingSummary?.(this.form);
      if (proposalNote) {
        customNotes.unshift(proposalNote);
      }

      // Raw meeting type value: either the generic in_person/phone radio choice,
      // or a preset value (e.g. "showroom"/"virtual") set via
      // setPresetMeetingType() before the popup opened. Falls back to
      // "in_person" so existing bookings without a meeting-type step keep
      // working exactly as before.
      const meetingType = (this.meetingTypeHidden?.value || 'in_person').trim();
      const meetingTypeLabel = (this.fieldMeetingType?.value || '').trim();
      // Note: for the generic 2-option flow, the step 2 gate (validateMeetingType)
      // guarantees a selection was made before this runs.

      // 300 matches the cap the Apps Script applies on the way in, so a long
      // list of pieces is not cut shorter than it has to be.
      const notes = customNotes.join('; ').substring(0, 300);

      return {
        action: 'book',
        secret: this.apiSecret,
        date: this.dateHidden?.value || '',
        time: this.timeHidden?.value || '',
        name: nameInput?.value?.trim() || '',
        email: emailInput?.value?.trim() || '',
        phone: this.phoneInput?.value?.trim() || '',
        product: this.fieldProduct?.value?.trim() || '',
        variant: this.fieldVariant?.value?.trim() || '',
        productUrl: this.fieldUrl?.value?.trim() || '',
        notes: notes,
        meetingType: meetingType,
        meetingTypeLabel: meetingTypeLabel,
        d: this.dateHidden?.value || '',
        t: this.timeHidden?.value || '',
        n: nameInput?.value?.trim() || '',
        e: emailInput?.value?.trim() || '',
        p: this.phoneInput?.value?.trim() || '',
        pr: this.fieldProduct?.value?.trim() || '',
        v: this.fieldVariant?.value?.trim() || '',
        u: this.fieldUrl?.value?.trim() || '',
        x: notes,
        m: meetingType,
        ml: meetingTypeLabel,
        companyEmail: this.customCompanyEmail ? '1' : '0',
        ce: this.customCompanyEmail ? '1' : '0',
      };
    }

    async reserveSlot() {
      const payload = this.collectBookingPayload();

      // One id per booking. If the first request is slow and we have to send
      // it again, the booking script recognises the id and returns the
      // original booking instead of saying the time is taken (which is what
      // used to show a "time slot" error even though the booking had gone
      // through and both emails were sent).
      const rid =
        window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID().replace(/-/g, '')
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      payload.rid = rid;

      let uncertain = false;
      let slotData = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const data = await this.jsonpRequest(payload, { timeout: 25000 });

          if (data && data.ok) {
            this.applyBookedResult(payload, data);
            return true;
          }

          if (data && data.error === 'slot_unavailable') {
            slotData = data;
            break;
          }

          // Any other error: the server may still have created the booking.
          uncertain = true;
        } catch (error) {
          // Timeout / lost reply: the booking may well have gone through.
          uncertain = true;
        }
      }

      // The request failed or its reply was lost, but the booking (and its
      // emails) can still have succeeded on the server. Ask the server about
      // this exact booking before telling the customer anything, so they are
      // never shown an error for a booking that exists (and never book twice).
      let status = { state: 'missing' };
      if (uncertain) {
        status = await this.confirmBookingStatus(rid);
        if (status.state === 'found') {
          this.applyBookedResult(payload, status.result);
          return true;
        }
      }

      if (slotData && status.state !== 'unknown') {
        this.bookedSlots = (slotData.booked || []).map((slot) => this.normalizeTime(slot)).filter(Boolean);
        apptCacheAvailability(payload.d, this.bookedSlots);
        this.renderTimeSlots();
        this.showError(this.querySelector('[data-appointment-time-picker]'), this.msgSlotUnavailable);
        this.showFormAlert(this.msgSlotUnavailable, { error: true });
        this.goToStep(1);
        return false;
      }

      this.showFormAlert(this.msgBookingError, { error: true });
      return false;
    }

    applyBookedResult(payload, data) {
      const bookedDate = payload.d;
      const bookedTime = this.normalizeTime(payload.t);
      apptMarkSlotBooked(bookedDate, bookedTime);
      apptInvalidateAvailability(bookedDate);
      this.bookedSlots = apptGetCachedAvailability(bookedDate) || [bookedTime];

      const calLink = data.calendarLink || data.eventUrl;
      this.lastCalendarLink = calLink || '';
      this.lastShopNotified = data.shopNotified === true;
      if (calLink) {
        try {
          sessionStorage.setItem(`appt-cal-${this.sectionId}`, calLink);
        } catch (error) {
          /* storage unavailable — the calendar link is still kept in memory */
        }
      }
    }

    // Asks the booking script whether the booking with this id exists.
    // Returns { state: 'found', result } | { state: 'missing' } | { state: 'unknown' }.
    async confirmBookingStatus(rid) {
      const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
      let sawMissing = false;

      for (let i = 0; i < 3; i += 1) {
        try {
          const data = await this.jsonpRequest(
            { action: 'bookingstatus', rid, secret: this.apiSecret },
            { timeout: 20000 }
          );

          if (!data || !data.ok) return { state: 'unknown' };
          if (data.found && data.result && data.result.ok) return { state: 'found', result: data.result };

          // Not recorded (yet). The original request may still be queued
          // behind other bookings, so look again shortly.
          sawMissing = true;
        } catch (error) {
          /* try again below */
        }
        if (i < 2) await wait(3000);
      }

      return { state: sawMissing ? 'missing' : 'unknown' };
    }

    // Runs on page load. If Shopify reloaded the page after a normal form
    // submit (the fallback path), the form renders a hidden marker instead of a
    // success message inside the popup. Turn that into the same outer-page
    // "booked" banner, and leave the form fully usable for another booking.
    showCalendarLinkIfSuccess() {
      const marker = this.querySelector('[data-appointment-posted]');
      if (!marker) return;
      marker.remove();

      // Several appointment forms can exist on one page; only show it once.
      if (window.__apptPostedHandled) return;
      window.__apptPostedHandled = true;

      const summary = { date: '', time: '', type: '', email: '' };
      let calendarLink = '';
      try {
        const raw = sessionStorage.getItem('appt-booked-summary');
        if (raw) Object.assign(summary, JSON.parse(raw));
        sessionStorage.removeItem('appt-booked-summary');
        calendarLink = sessionStorage.getItem(`appt-cal-${this.sectionId}`) || '';
        sessionStorage.removeItem(`appt-cal-${this.sectionId}`);
      } catch (error) {
        /* storage unavailable — banner still shows without the extra details */
      }

      // Drop ?contact_posted=true so a refresh does not repeat the banner.
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('contact_posted')) {
          url.searchParams.delete('contact_posted');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
      } catch (error) {
        /* ignore */
      }

      this.showBookedBanner(summary, {
        modal: this.closest('modal-dialog'),
        opener: null,
        calendarLink,
      });
    }

    syncHiddenFields() {
      if (this.fieldDate && this.dateHidden) {
        this.fieldDate.value = this.dateHidden.value;
      }
      if (this.fieldTime && this.timeHidden) {
        this.fieldTime.value = this.timeHidden.value;
      }
    }

    // Remove the name from any empty optional contact field right before the
    // form navigates away, so blank values (e.g. no phone, no variant) are not
    // submitted to Shopify and therefore never appear in the contact email.
    // Runs only immediately before submit, so it never affects retries.
    stripEmptyContactFields() {
      if (!this.form) return;
      const fields = this.form.querySelectorAll(
        'input[name^="contact["], select[name^="contact["], textarea[name^="contact["]'
      );
      fields.forEach((field) => {
        const type = (field.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') return;
        if (field.name === 'contact[email]') return;
        if (!field.value || !field.value.trim()) {
          field.removeAttribute('name');
        }
      });
    }

    syncCheckboxFields() {
      this.querySelectorAll('[data-appointment-checkbox-hidden]').forEach((hidden) => {
        const group = hidden.closest('[data-appointment-custom-input-group]');
        const checked = group?.querySelectorAll('[data-appointment-custom-checkbox]:checked') || [];
        hidden.value = Array.from(checked)
          .map((input) => input.value)
          .join(', ');
      });
    }

    updateFromVariant(variant, html) {
      if (this.fieldVariant) {
        this.fieldVariant.value = variant?.title || '';
      }
      if (this.variantTitle && variant?.title) {
        this.variantTitle.textContent = variant.title;
      }
      if (html && this.productPrice) {
        const priceSource = html.getElementById(`price-${this.variantSourceSectionId}`);
        if (priceSource) {
          this.productPrice.innerHTML = priceSource.innerHTML;
        }
      }
    }

    goToStep(step) {
      // Skip-meeting-type mode: jump straight from step 1 to step 3 (and back),
      // so the meeting-type step is never activated.
      if (this.skipMeetingType && step === 2) {
        step = this.currentStep === 1 ? 3 : 1;
      }

      // Only validate when moving forward, gating each step by the one being left.
      if (step > this.currentStep) {
        if (this.currentStep === 1) {
          if (!this.validateStep1()) return;
          if (this.hasAvailabilityApi && this.bookedSlots.includes(this.normalizeTime(this.selectedTime))) {
            this.showFormAlert(this.msgSlotUnavailable, { error: true });
            return;
          }
        } else if (this.currentStep === 2) {
          if (!this.validateMeetingType()) return;
        }
      }

      this.currentStep = step;
      this.steps.forEach((el) => {
        const stepNum = Number(el.dataset.appointmentStep);
        const isActive = stepNum === step;
        el.classList.toggle('is-active', isActive);
        el.hidden = !isActive;
      });

      this.indicators.forEach((el) => {
        const stepNum = Number(el.dataset.stepIndicator);
        el.classList.toggle('is-active', stepNum === step);
        el.classList.toggle('is-complete', stepNum < step);
      });

      this.scrollActiveStepToTop();
    }

    scrollActiveStepToTop() {
      // Without this, switching steps keeps the previous scroll position. On
      // mobile the user scrolls down through step 1 (date/time), so step 2 would
      // open already scrolled past its lower fields (custom "additional info"
      // fields and the submit button), making them look hidden.
      const scrollContainer = this.getScrollContainer();
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';

      window.requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior });
          return;
        }

        // Inline (page) version: only pull the form up if its top is off-screen,
        // so we don't yank the page when everything already fits.
        const rect = this.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.4) {
          this.scrollIntoView({ block: 'start', behavior, inline: 'nearest' });
        }
      });
    }

    validateStep1() {
      this.clearErrors();

      if (!this.selectedDate || !this.dateHidden?.value) {
        this.showError(this.querySelector('[data-appointment-date-picker]'), 'Please select a date.');
        return false;
      }
      if (this.hasAvailabilityApi && !this.availabilityReady) {
        this.showError(this.timePickerSection, this.msgSlotsLoading);
        return false;
      }
      if (this.isClosedDay(this.selectedDate)) {
        this.showError(this.querySelector('[data-appointment-date-picker]'), this.closedDayMessage);
        return false;
      }
      if (!this.selectedTime || !this.timeHidden?.value) {
        this.showError(this.querySelector('[data-appointment-time-picker]'), 'Please select a time.');
        return false;
      }
      if (this.hasAvailabilityApi && this.bookedSlots.includes(this.normalizeTime(this.selectedTime))) {
        this.showError(this.querySelector('[data-appointment-time-picker]'), this.msgSlotUnavailable);
        return false;
      }
      return true;
    }

    validateMeetingType() {
      if (!this.meetingTypeOptions || !this.meetingTypeOptions.length) return true;

      this.clearErrors();
      const chosen = this.meetingTypeHidden?.value;
      if (chosen !== 'in_person' && chosen !== 'phone') {
        const section = this.querySelector('[data-appointment-meeting-type-section]');
        this.showError(section, this.msgMeetingType);
        return false;
      }
      return true;
    }

    validateStep2() {
      this.clearErrors();
      let valid = true;

      const phone = this.phoneInput;
      if (this.isPhoneRequired() && phone && !phone.value.trim()) {
        this.showError(phone.closest('.field'), 'Please enter your phone number.');
        valid = false;
      }

      this.querySelectorAll('[data-appointment-custom-field]').forEach((field) => {
        const required = field.dataset.fieldRequired === 'true';
        const type = field.dataset.fieldType;

        if (!required) return;

        if (type === 'checkbox') {
          const checked = field.querySelectorAll('[data-appointment-custom-checkbox]:checked');
          if (!checked.length) {
            this.showError(field, 'Please select at least one option.');
            valid = false;
          }
        } else if (type === 'radio') {
          const selected = field.querySelector('[type="radio"]:checked');
          if (!selected) {
            this.showError(field, 'Please select an option.');
            valid = false;
          }
        } else {
          const input = field.querySelector('[data-appointment-custom-input], select, input[type="text"]');
          if (input && !input.value.trim()) {
            this.showError(field, 'This field is required.');
            valid = false;
          }
        }
      });

      return valid;
    }

    showError(container, message) {
      if (!container || !message) return;
      const error = document.createElement('span');
      error.className = 'appointment-field-error';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      container.appendChild(error);
      error.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    clearErrors() {
      this.querySelectorAll('.appointment-field-error').forEach((el) => el.remove());
    }

    // A phone number is needed whenever we have to call the customer: always
    // when the section sets "contact required", and for Phone call and Virtual
    // appointments (we need a number to call / to share the meeting details).
    isPhoneRequired() {
      if (this.contactRequired) return true;
      const type = (this.meetingTypeHidden?.value || '').trim().toLowerCase();
      return type === 'phone' || type === 'virtual';
    }

    updatePhoneRequirement() {
      const phone = this.phoneInput;
      if (!phone) return;
      const required = this.isPhoneRequired();

      phone.required = required;
      phone.setAttribute('aria-required', required ? 'true' : 'false');

      const mark = this.querySelector('[data-appointment-phone-mark]');
      if (mark) mark.hidden = !required;
      const hint = this.querySelector('[data-appointment-phone-optional]');
      if (hint) hint.hidden = required;
    }

    resetMeetingType() {
      if (this.meetingTypeOptions && this.meetingTypeOptions.length) {
        this.meetingTypeOptions.forEach((el) => {
          el.classList.remove('is-selected');
          el.setAttribute('aria-checked', 'false');
        });
      }
      if (this.meetingTypeHidden) this.meetingTypeHidden.value = '';
      if (this.fieldMeetingType) this.fieldMeetingType.value = '';
      this.updatePhoneRequirement();
    }

    resetForm() {
      this.goToStep(1);
      this.selectedDate = null;
      this.selectedTime = null;
      this.bookedSlots = [];
      this.availabilityLoading = false;
      this.availabilityReady = !this.hasAvailabilityApi;
      this.isSubmitting = false;
      this.calendarMonth = new Date();
      if (this.dateHidden) this.dateHidden.value = '';
      if (this.timeHidden) this.timeHidden.value = '';
      if (this.calSelected) this.calSelected.hidden = true;
      this.resetMeetingType();
      // Cards that pre-select the meeting type (Visit options) have no
      // meeting-type step, so keep their choice for the next booking.
      if (this.presetMeetingType) {
        this.setPresetMeetingType(this.presetMeetingType.value, this.presetMeetingType.label);
      }
      this.setSubmitLoading(false);
      this.renderTimeSlots();
      this.renderCalendar();
      this.updateNextButtonState();
      this.clearErrors();
      this.clearFormAlerts();
    }
  }

  customElements.define('appointment-booking-form', AppointmentBookingForm);
}
